/** Reine Darstellungslogik von Schritt 6 „Nachrichten“ (Ablaufliste und „Flow testen“). */

import { convertToSeconds, splitTimeInSeconds } from '../../../../../utils/time-parts.utils';
import { FlowRow, WizardCandidate } from '../../campaign-wizard.types';

/** Einrückung je Verzweigungstiefe (Design: `indent(d) = d * 32px`). */
export const rowIndentPx = (depth: number): number => Math.max(0, depth) * 32;

export type TestStatus = 'idle' | 'loading' | 'done' | 'stale' | 'error';

/**
 * Ein fertiger Test wird „stale“, sobald sich Profil, Szenario oder Antworten (z. B. Anzahl Chat-Nachrichten)
 * gegenüber dem Stand des Tests geändert haben.
 */
export const effectiveTestStatus = (
  status: TestStatus,
  testedKey: string | null,
  currentKey: string
): TestStatus => (status === 'done' && testedKey !== currentKey ? 'stale' : status);

/** Button-Beschriftung: „Flow testen“ nur, solange noch nie getestet wurde. */
export const testButtonLabelKey = (status: TestStatus): 'wizMsgTestRun' | 'wizMsgTestRerun' =>
  status === 'idle' ? 'wizMsgTestRun' : 'wizMsgTestRerun';

/** Initialen aus Vor- und Nachname („Markus Weber“ → „MW“). */
export const candidateInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
  return (first + last).toUpperCase();
};

/** Firma des Kandidaten steht auf einer Blacklist → wird nicht angesprochen. */
export const isCompanyBlocked = (candidate: Pick<WizardCandidate, 'companyBlockedBy'>): boolean =>
  candidate.companyBlockedBy.length > 0;

export const testSubtitle = (candidate: WizardCandidate, blockedText: string): string =>
  isCompanyBlocked(candidate)
    ? blockedText
    : [candidate.title, candidate.company].filter(Boolean).join(' · ');

export interface PlanSummary {
  msgCount: number;
  inmails: number;
}

/** Zeilen, die in der Ablaufliste als Nachricht gezählt werden. */
export const countMessages = (rows: FlowRow[]): PlanSummary => ({
  msgCount: rows.filter(r => r.kind === 'msg').length,
  inmails: rows.filter(r => r.kind === 'msg' && r.isInmail).length,
});

/** Stand des Tests: Antworten (inkl. Wartezeiten) und Profil. Ändert sich einer, ist ein fertiges Ergebnis überholt. */
export const testKey = (answersKey: string, candidateId: string): string => `${answersKey}|${candidateId}`;

/* ------------------------------------------------------------------ */
/* Wartezeiten (Tage / Stunden / Minuten wie im alten Flow-Builder)     */
/* ------------------------------------------------------------------ */

export const MINUTES_PER_DAY = 24 * 60;
export const WAIT_MAX_MINUTES = 90 * MINUTES_PER_DAY;
/** Das Zeitfenster „Kontaktanfrage akzeptiert?“ ist mindestens ein Tag, jede andere Wartezeit mindestens eine Minute. */
export const ACCEPT_WINDOW_KEY = 'accept_window';
export const minWaitMinutes = (waitKey: string): number => (waitKey === ACCEPT_WINDOW_KEY ? MINUTES_PER_DAY : 1);

export interface WaitParts {
  days: number;
  hours: number;
  minutes: number;
}

/** Minuten → Tage/Stunden/Minuten mit denselben Helfern wie die Wartezeit-Knoten des alten Flow-Builders (Sekunden). */
export const splitWaitMinutes = (totalMinutes: number): WaitParts =>
  splitTimeInSeconds(Math.max(0, Math.round(totalMinutes)) * 60);

export const joinWaitMinutes = (parts: WaitParts): number =>
  Math.round(convertToSeconds({ days: parts.days || 0, hours: parts.hours || 0, minutes: parts.minutes || 0 }) / 60);

/** Einheit im Plural, optional mit eigener Einzahl für genau 1 („1 Tag“, „1 Stunde“, „1 Minute“). */
export interface WaitUnit {
  one: string;
  other: string;
}
export type WaitUnits = Record<'days' | 'hours' | 'minutes', string | WaitUnit>;

const unitFor = (unit: string | WaitUnit, count: number): string =>
  typeof unit === 'string' ? unit : count === 1 ? unit.one : unit.other;

/** „2 Tage 4 Stunden 5 Minuten“ (nur Teile > 0, Einzahl bei 1), die Einheiten kommen von außen (Übersetzung). */
export const formatWaitParts = (totalMinutes: number, units: WaitUnits): string => {
  const p = splitWaitMinutes(totalMinutes);
  const parts = [
    p.days ? `${p.days} ${unitFor(units.days, p.days)}` : '',
    p.hours ? `${p.hours} ${unitFor(units.hours, p.hours)}` : '',
    p.minutes ? `${p.minutes} ${unitFor(units.minutes, p.minutes)}` : '',
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : `0 ${unitFor(units.minutes, 0)}`;
};

/** Eingabefeld → ganze Zahl ≥ 0 (leer/ungültig = 0). */
export const parseWaitPart = (raw: string): number => {
  const n = Math.floor(Number(raw));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export type WaitCheck = 'ok' | 'tooShort' | 'tooLong';

export const checkWaitMinutes = (waitKey: string, minutes: number): WaitCheck =>
  minutes < minWaitMinutes(waitKey) ? 'tooShort' : minutes > WAIT_MAX_MINUTES ? 'tooLong' : 'ok';

/**
 * Neuer Stand der eigenen Wartezeiten nach einer Eingabe. Entspricht die Zeit der Vorgabe, fällt der Eintrag weg
 * (so bleibt der Ablauf-Schlüssel gleich, solange nichts wirklich geändert ist); ohne Einträge gibt es kein Objekt.
 */
export const withWaitMinutes = (
  current: Record<string, number> | undefined,
  waitKey: string,
  minutes: number,
  defaultMinutes: number
): Record<string, number> | undefined => {
  const next = { ...(current ?? {}) };
  if (minutes === defaultMinutes) delete next[waitKey];
  else next[waitKey] = minutes;
  return Object.keys(next).length > 0 ? next : undefined;
};

/** Zeile der Ablaufliste, deren Zeit sich einstellen lässt. */
export const isEditableWait = (row: FlowRow): boolean => Boolean(row.waitKey) && typeof row.minutes === 'number';

/* ------------------------------------------------------------------ */
/* Abfrage des laufenden Tests                                          */
/* ------------------------------------------------------------------ */

export const TEST_POLL_MS = 3000;
/** Längstens so lange fragt die Karte nach (der Server gibt nach 10 Minuten auf). */
export const TEST_POLL_MAX_MS = 10 * 60 * 1000;
/** Der Ablauf wird noch gespeichert: nach so vielen Sekunden Versuchen aufgeben. */
export const TEST_PREPARE_MAX_TRIES = 10;

/** „Der Test ist fehlgeschlagen.“ plus Grund – das Präfix steht genau einmal, auch wenn der Grund es schon enthält. */
export const testErrorText = (prefix: string, reason: string | null | undefined): string => {
  let cleaned = String(reason ?? '').trim();
  while (cleaned.toLowerCase().startsWith(prefix.toLowerCase())) cleaned = cleaned.slice(prefix.length).trim();
  return cleaned ? `${prefix} ${cleaned}` : prefix;
};

/**
 * Anzeige des Ablaufs in Schritt 6: bis der Plan da ist „wird berechnet“; der Fehlertext erst nach einem echten Fehler
 * (nicht schon im ersten Moment nach dem Laden, wenn noch nichts angefragt wurde).
 */
export const planViewState = (input: { loading: boolean; hasPlan: boolean; failed: boolean }): 'plan' | 'loading' | 'error' =>
  input.hasPlan ? 'plan' : input.loading || !input.failed ? 'loading' : 'error';
